import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
  HttpEvent,
  HttpRequest,
  HttpHandlerFn
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, tap, from, switchMap, Observable } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { environment } from '@root/environments/environment';
import { MachineInfoService } from '@app/core/services/machine-info.service';
import { AuthService } from '@app/core/services/auth.service';

export const httpInterceptor: HttpInterceptorFn =
  (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {

    const authService = inject(AuthService);
    const notificationService = inject(NotificationService);
    const machineInfoService = inject(MachineInfoService);

    const applyCsrf = (request: HttpRequest<unknown>, token?: string | null) => {
      const isAuthCallback = request.url.startsWith(`${environment.baseURL}/callback`);
      const baseHeaders: Record<string, string> = isAuthCallback
        ? {}
        : { 'X-Requested-With': 'XMLHttpRequest' };
      if (token) {
        return request.clone({
          withCredentials: true,
          setHeaders: { ...baseHeaders, 'X-CSRF-Token': token }
        });
      }
      return request.clone({ withCredentials: true, setHeaders: baseHeaders });
    };

    let csrfToken = authService.getCsrfToken();
    const requestWithCsrf = applyCsrf(req, csrfToken);

    /**
     * Обработка успешного ответа:
     * если пришёл новый CSRF-токен — обновляем его в AuthService.
     */
    const handleResponse = () =>
      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          const newToken = event.headers.get('X-CSRF-Token');
          if (newToken && newToken !== csrfToken) {
            csrfToken = newToken;
            authService.updateCsrfToken(newToken);
          }
        }
      });

    /**
     * Обработка ошибок:
     *  - 403: пробуем взять новый токен из заголовка, обновить и переотправить запрос
     *  - 401: показываем сообщение и уводим пользователя в неавторизованное состояние
     */
    const handleError = (originalRequest: HttpRequest<unknown>) =>
      catchError((error: HttpErrorResponse) => {
        // 403 Forbidden
        if (error.status === 403) {
          const newToken = error.headers.get('X-CSRF-Token');

          if (newToken && newToken !== csrfToken) {
            // Обновляем CSRF-токен
            csrfToken = newToken;
            authService.updateCsrfToken(newToken);

            // Повторяем запрос с новым токеном
            const retryRequest = applyCsrf(originalRequest, newToken);

            return next(retryRequest).pipe(handleResponse());
          }

          // Если нового токена нет — просто показываем ошибку доступа
          notificationService.notify(
            'У вас нет прав доступа к этому ресурсу.',
            'danger'
          );
        }

        // 401 Unauthorized
        if (error.status === 401) {
          notificationService.notify(
            'Ошибка авторизации. Пожалуйста, войдите снова.',
            'danger'
          );
          authService.handleUnauthorized(); // или authService.onLogout(), если так у тебя называется
        }

        return throwError(() => error);
      });

    /**
     * Хелпер, который отправляет запрос и вешает общие обработчики
     */
    const sendRequest = (finalReq: HttpRequest<unknown>) =>
      next(finalReq).pipe(
        handleResponse(),
        handleError(finalReq)
      );

    // Если плагин машины выключен — просто шлём запрос с CSRF
    if (!environment.enableMachinePlugin) {
      return sendRequest(requestWithCsrf) as Observable<HttpEvent<unknown>>;
    }

    // Если плагин включён — сначала подтягиваем данные машины и добавляем их в заголовки
    return from(machineInfoService.getMachineInfo()).pipe(
      switchMap(info => {
        const updatedHeaders = requestWithCsrf.headers
          .set('X-IP', info.ip)
          .set('X-MAC-Address', info.mac)
          .set('X-CPU-ID', info.cpuId);

        const newReq = requestWithCsrf.clone({ headers: updatedHeaders });
        return sendRequest(newReq);
      })
    ) as Observable<HttpEvent<unknown>>;
  };

export default httpInterceptor;
